import { Star } from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { IgAnalyticsTooltip } from "@intelligems/ig-theme/IgAnalyticsTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { STANDARD_EVENTS_ALLOWED_KEY_METRICS } from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { findTooltip } from "@src/features/analytics/baseDashboard/utils";
import { chartTooltips } from "@src/features/analytics/utils";
import { getPrimaryMetricId } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";

interface HeaderDetailsProps {
  config: GraphConfig;
  metricFilterIndex?: number;
}

export const HeaderDetails = ({
  config,
  metricFilterIndex,
}: HeaderDetailsProps) => {
  const theme = useTheme();
  const experience = useSelector(selectedExperience);

  const title = config?.title?.[metricFilterIndex || 0] ?? config?.title?.[0];
  const primaryMetricId = useSelector(getPrimaryMetricId);

  const showPrimaryStar = useMemo(() => {
    if (!experience || experience.category === "personalization") {
      return false;
    }
    const metricToUse = config?.impactMetric ?? config?.metrics[0];
    return (
      (STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryMetricId]
        ?.analyticsMetricId || primaryMetricId) === metricToUse
    );
  }, []);

  // With nothing to explain the tooltip only repeats the title, so the
  // heading goes bare instead.
  const description =
    config.titleTooltip ||
    findTooltip(
      config,
      chartTooltips,
      experience?.category ?? "experiment",
      metricFilterIndex
    );

  const heading = (
    <IgTypography fontWeight={600} variant="h3">
      {title}
    </IgTypography>
  );

  const titleRow = (
    <Stack direction={"row"} alignItems={"center"} gap={1}>
      {showPrimaryStar && (
        <IgAnalyticsTooltip title={"Primary Goal"}>
          <Star size={18} color={theme.igPrimitives.yellow[500]} />
        </IgAnalyticsTooltip>
      )}

      {title &&
        (description ? (
          <IgAnalyticsTooltip
            title={
              <IgTypography>
                <IgTypography fontWeight={600}>{title}</IgTypography>
                <br />
                {description}
              </IgTypography>
            }
          >
            {heading}
          </IgAnalyticsTooltip>
        ) : (
          heading
        ))}
    </Stack>
  );

  // Only charts that set a subtitle get the extra wrapper, so every other
  // header keeps the row it has always been.
  if (!config.subtitle) {
    return titleRow;
  }

  return (
    <Stack gap={1}>
      {titleRow}
      <IgTypography
        sx={{ color: theme.palette.text.secondary, textWrap: "pretty" }}
        variant={"body2"}
      >
        {config.subtitle}
      </IgTypography>
    </Stack>
  );
};
